// Routes — the policies that sit between a number and the phones.
//
// A number pointed straight at an extension rings one phone. That's most of a
// residential book and none of a business one: a shop wants its main line to ring
// the counter and the office together, an office wants an auto attendant. Those
// policies had no home in the dashboard, so a multi-phone customer couldn't be
// configured here at all.
//
// The page answers three questions per row, in this order, because that's the
// order an operator asks them: what kind of route is it, what does it ring, and
// which numbers reach it. A route no number points at is called out — it rings
// nobody, and that's nearly always a half-finished setup rather than a choice.
//
// Ring groups are the only type Verso builds today. The API's `kinds` catalogue
// names the others — auto attendant, queue, schedule — and marks them
// `modeled: false`, so "New route" can list what a route can be and say which
// types are still managed on the PBX. A menu with one item and no explanation
// leaves the operator wondering whether the feature exists.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin } from '../lib/roles.js';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import RouteEditor from '../components/routes/RouteEditor.jsx';

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'live',      label: 'In use' },
  { key: 'unrouted',  label: 'No number' },
  { key: 'empty',     label: 'Rings nobody' },
  { key: 'unsynced',  label: 'Not on the PBX' },
];

const fmtDid = (e164) => {
  const s = String(e164 || '').replace(/[^0-9]/g, '');
  if (s.length === 11 && s[0] === '1') return `+1 (${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
  return String(e164 || '');
};

const STRATEGY_LABELS = {
  ringall: 'all at once',
  hunt: 'in order',
  memoryhunt: 'in order, cumulative',
  firstavailable: 'first available',
  firstnotonphone: 'first not on a call',
  random: 'random',
};

// One colour per type, so a mixed table can be read down the Type column at a
// glance rather than word by word.
const KIND_TONES = {
  ring_group:     'bg-blue-50 text-blue-700 ring-blue-200',
  ivr:            'bg-purple-50 text-purple-700 ring-purple-200',
  queue:          'bg-teal-50 text-teal-700 ring-teal-200',
  time_condition: 'bg-slate-100 text-slate-700 ring-slate-200',
};

function KindBadge({ kind, kinds }) {
  const meta = kinds[kind] || {};
  return (
    <span
      title={meta.blurb || undefined}
      className={`inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        KIND_TONES[kind] || 'bg-gray-100 text-gray-700 ring-gray-200'
      }`}
    >
      {meta.label || kind}
    </span>
  );
}

// "New route" — one entry per type in the API's catalogue. Types Verso can't
// build yet are listed and disabled with the reason, which is the honest answer
// to "where's the auto attendant?" and stops the question being asked twice.
function NewRouteMenu({ kinds, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const entries = Object.entries(kinds);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Before the catalogue arrives there's nothing honest to offer.
  if (entries.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
      >
        New route
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
          <path d="M5.5 7.5l4.5 5 4.5-5z" />
        </svg>
      </button>

      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-80 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {entries.map(([kind, meta]) => (
            <button
              key={kind}
              type="button"
              role="menuitem"
              disabled={!meta.modeled}
              title={meta.modeled ? undefined : meta.unavailable}
              onClick={() => { setOpen(false); onPick(kind); }}
              className="block w-full px-3 py-2 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-white"
            >
              <span className={`text-sm font-medium ${meta.modeled ? 'text-gray-900' : 'text-gray-400'}`}>
                {meta.label}
              </span>
              <span className={`block text-xs ${meta.modeled ? 'text-gray-500' : 'text-gray-400'}`}>
                {meta.modeled ? meta.blurb : meta.unavailable}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'plain', hint }) {
  const tones = {
    plain: 'text-gray-900',
    warn:  'text-amber-700',
    bad:   'text-red-700',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

// Whether the PBX has this policy yet. Verso is source-of-truth for the desired
// state, but a number can't be pointed at a group FreePBX doesn't have — so this
// is load-bearing, not decoration.
function SyncBadge({ status }) {
  const map = {
    synced:  { cls: 'bg-green-100 text-green-700', text: 'on the PBX' },
    pending: { cls: 'bg-amber-100 text-amber-800', text: 'not on the PBX yet' },
    error:   { cls: 'bg-red-100 text-red-700',     text: 'sync failed' },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.text}</span>;
}

export default function RoutesList() {
  const { role } = useAuth();
  const canWrite = canAdmin(role);
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);   // route object, or { kind } for a new one
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['routes'],
    queryFn: () => api.get('/routes'),
  });

  const routes = useMemo(() => data?.routes || [], [data]);
  const kinds = data?.kinds || {};
  const summary = data?.summary || { total: 0, by_kind: {}, unrouted: 0, empty: 0, unsynced: 0 };

  const remove = useMutation({
    mutationFn: (r) => api.delete(`/ring-groups/${r.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      setDeleting(null);
    },
  });

  const counts = useMemo(() => ({
    all: routes.length,
    live: routes.filter((r) => r.dids.length > 0).length,
    unrouted: routes.filter((r) => r.dids.length === 0).length,
    empty: routes.filter((r) => r.targets.length === 0).length,
    unsynced: routes.filter((r) => r.syncStatus !== 'synced').length,
  }), [routes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return routes.filter((r) => {
      if (filter === 'live'     && r.dids.length === 0) return false;
      if (filter === 'unrouted' && r.dids.length > 0) return false;
      if (filter === 'empty'    && r.targets.length > 0) return false;
      if (filter === 'unsynced' && r.syncStatus === 'synced') return false;
      if (!q) return true;
      // Searchable by everything on the row a person would remember it by,
      // including a number that rings it.
      return [r.name, r.number, r.subscriberName, ...(r.targets || []), ...(r.dids || [])]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [routes, filter, search]);

  const { paged: rows, pagination } = usePagination(filtered);

  const [importResult, setImportResult] = useState(null);
  const importGroups = useMutation({
    mutationFn: () => api.post('/ring-groups/import', {}),
    onSuccess: (res) => {
      setImportResult(res);
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['ring-groups'] });
    },
    onError: (err) => setImportResult({ error: err?.body?.error || err?.message || 'Import failed' }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Routes</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            How a number reaches more than one phone. Point a number at a route on its own page.
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Most orgs have ring groups on the PBX before they have them in
                Verso. Reading them in is safe — nothing is written to any PBX —
                and re-runnable, so it converges instead of duplicating. */}
            <button
              onClick={() => importGroups.mutate()}
              disabled={importGroups.isPending}
              title="Read the ring groups that already exist on this org’s PBX hosts into Verso. Nothing is written to the PBX."
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {importGroups.isPending ? 'Importing…' : 'Import from PBX'}
            </button>
            <NewRouteMenu kinds={kinds} onPick={(kind) => setEditing({ kind })} />
          </div>
        )}
      </div>

      {importResult && (
        <div className={`rounded-md border px-3 py-2 text-sm ${
          importResult.error ? 'border-red-200 bg-red-50 text-red-800' : 'border-blue-200 bg-blue-50 text-blue-900'
        }`}>
          {importResult.error ? importResult.error : (
            <>
              Found {importResult.found} on the PBX — {importResult.imported} new, {importResult.updated} updated.
              {importResult.unassigned?.length > 0 && (
                <> {importResult.unassigned.length} need a subscriber chosen by hand
                  (<span className="font-mono">{importResult.unassigned.join(', ')}</span>) — their members
                  don’t all belong to one customer.</>
              )}
              {(importResult.hosts || []).filter((h) => !h.ok).map((h) => (
                <div key={h.agentId} className="mt-1 text-amber-900">{h.name}: {h.error}</div>
              ))}
            </>
          )}
          <button onClick={() => setImportResult(null)} className="ml-2 underline hover:no-underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Routes" value={summary.total} />
        <StatCard
          label="No number" value={summary.unrouted}
          tone={summary.unrouted ? 'warn' : 'plain'}
          hint={summary.unrouted ? 'nothing reaches these' : undefined}
        />
        <StatCard
          label="Rings nobody" value={summary.empty}
          tone={summary.empty ? 'bad' : 'plain'}
          hint={summary.empty ? 'no extensions in the route' : undefined}
        />
        <StatCard
          label="Not on the PBX" value={summary.unsynced}
          tone={summary.unsynced ? 'warn' : 'plain'}
          hint={summary.unsynced ? 'numbers can’t point at these yet' : undefined}
        />
      </div>

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by name, number, extension…"
        filters={FILTERS}
        filterCounts={counts}
        activeFilter={filter}
        onFilterChange={setFilter}
      />

      {isLoading && <LoadingSpinner />}
      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error?.body?.error || error?.message || 'Could not load routes'}
        </p>
      )}

      {!isLoading && !error && routes.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center">
          <p className="text-sm font-medium text-gray-900">No routes yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            A route lets one number reach more than one phone. Ring groups are built here — start
            one with New route, then point a number at it from the number&rsquo;s page. Auto
            attendants, queues and schedules are still managed on the PBX.
          </p>
        </div>
      )}

      {!isLoading && !error && routes.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Route</th>
                {/* No PBX column: a route inherits its host from the DID that
                    points at it. "On the PBX" below still says whether it was
                    actually built — that's state, not an assignment. */}
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Rings</th>
                <th className="px-4 py-2 font-medium">Numbers</th>
                <th className="px-4 py-2 font-medium">On the PBX</th>
                <th className="w-10 px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={`${r.kind}:${r.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5"><KindBadge kind={r.kind} kinds={kinds} /></td>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-gray-900">{r.name || `Route ${r.number}`}</span>
                    <span className="ml-2 font-mono text-xs text-gray-500">{r.number}</span>
                    {r.kind === 'ring_group' && (
                      <span className="block text-xs text-gray-400">
                        {STRATEGY_LABELS[r.config?.strategy] || r.config?.strategy}
                        {r.config?.ringTime != null && ` · ${r.config.ringTime}s`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{r.pbxName || '—'}</td>
                  <td className="px-4 py-2.5">
                    {r.subscriberId
                      ? <Link to={`/subscribers/${r.subscriberId}`} className="text-blue-600 hover:text-blue-700">
                          {r.subscriberName || `#${r.subscriberId}`}
                        </Link>
                      : <span className="text-gray-400">shared</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.targets.length === 0
                      ? <span className="text-red-700">nobody</span>
                      : (
                        <span className="font-mono text-xs text-gray-700">
                          {r.targets.join(', ')}
                        </span>
                      )}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.dids.length === 0
                      ? <span className="text-xs text-amber-700">none point here</span>
                      : (
                        <span className="text-xs text-gray-700">
                          {r.dids.map((d, i) => (
                            <span key={d}>
                              {i > 0 && ', '}
                              <Link to={`/dids/${String(d).replace(/^\+/, '')}`} className="text-blue-600 hover:text-blue-700">
                                {fmtDid(d)}
                              </Link>
                            </span>
                          ))}
                        </span>
                      )}
                  </td>
                  <td className="px-4 py-2.5"><SyncBadge status={r.syncStatus} /></td>
                  <td className="px-4 py-2.5 text-right">
                    {canWrite && (
                      <ActionMenu
                        items={[
                          { label: 'Edit route', onClick: () => setEditing(r) },
                          {
                            label: 'Delete route', danger: true,
                            onClick: () => setDeleting(r),
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination {...pagination} />
        </div>
      )}

      {editing && (
        <RouteEditor
          route={editing.id ? editing : null}
          kind={editing.kind || 'ring_group'}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['routes'] });
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <ConfirmDeleteDialog
          title={`Delete ${deleting.name || `route ${deleting.number}`}?`}
          // Numbers pointing here are the consequence worth naming — they'd be
          // left ringing a destination that no longer exists.
          body={deleting.dids.length
            ? `${deleting.dids.length} number${deleting.dids.length === 1 ? '' : 's'} still point here `
              + `(${deleting.dids.map(fmtDid).join(', ')}). They will have no destination until you `
              + 'point them somewhere else.'
            : 'No numbers point here, so nothing loses its destination.'}
          confirmLabel="Delete route"
          error={remove.error?.body?.error || remove.error?.message}
          isPending={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </div>
  );
}
